import { Link } from "react-router-dom";
import { LANG_LABEL } from "../lib/categories";

export function ArticleCard({ article, variant = "default" }) {
  // article.category is set by backend serializer; fallback to slug-only
  const cat = article.category || (article.category_slug ? { slug: article.category_slug, name: article.category_slug } : null);
  const langLabel = LANG_LABEL[article.language] || article.language?.toUpperCase();

  if (variant === "hero") {
    return (
      <Link to={`/article/${article.slug}`} className="block group" data-testid={`article-hero-${article.slug}`}>
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono-label bg-[#4A7A3D] text-[#FAF8F3] px-2 py-1">À LA UNE</span>
              {cat && <span className="font-mono-label text-[#4A7A3D]">{cat.name}</span>}
              {langLabel && <span className="font-mono-label text-[#4B4B48]">{langLabel}</span>}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] text-[#1A1A18] group-hover:text-[#4A7A3D]">{article.title}</h1>
            {article.subtitle && (
              <p className="mt-5 font-quote text-xl md:text-2xl text-[#4B4B48] leading-snug max-w-2xl">{article.subtitle}</p>
            )}
            <div className="mt-6 flex items-center gap-4 font-mono-label text-[#4B4B48]">
              <span>{article.reading_time || 5} min de lecture</span>
              <span className="text-[#E0D8CE]">·</span>
              <span>Lire l'article →</span>
            </div>
          </div>
          <div className="md:col-span-5 order-1 md:order-2">
            <div className="aspect-[4/5] overflow-hidden bg-[#F5EFEB]">
              {article.featured_image && <img src={article.featured_image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" loading="eager" />}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link to={`/article/${article.slug}`} className="grid md:grid-cols-2 gap-6 group card-shift p-4 md:p-6 border border-[#E0D8CE]" data-testid={`article-wide-${article.slug}`}>
        <div className="aspect-[16/10] overflow-hidden bg-[#F5EFEB]">
          {article.featured_image && <img src={article.featured_image} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />}
        </div>
        <div className="flex flex-col justify-center">
          {cat && <span className="font-mono-label text-[#4A7A3D] mb-3">{cat.name}</span>}
          <h3 className="font-display text-2xl md:text-3xl leading-[1.05] text-[#1A1A18] group-hover:text-[#4A7A3D]">{article.title}</h3>
          {article.subtitle && <p className="mt-3 text-[#4B4B48] line-clamp-3">{article.subtitle}</p>}
          <div className="mt-4 font-mono-label text-[#4B4B48]">{article.reading_time} min · {langLabel}</div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link to={`/article/${article.slug}`} className="block group py-4 border-b border-[#E0D8CE]" data-testid={`article-compact-${article.slug}`}>
        {cat && <span className="font-mono-label text-[#4A7A3D]">{cat.name}</span>}
        <h3 className="font-display text-xl md:text-2xl leading-[1.05] text-[#1A1A18] group-hover:text-[#4A7A3D] mt-1">{article.title}</h3>
        <div className="mt-2 font-mono-label text-[#4B4B48]">{article.reading_time || 4} min · {langLabel}</div>
      </Link>
    );
  }

  if (variant === "onDark") {
    return (
      <Link to={`/article/${article.slug}`} className="group block bg-[#FAF8F3] text-[#1A1A18] card-shift" data-testid={`article-card-${article.slug}`}>
        <div className="aspect-[4/3] overflow-hidden bg-[#F5EFEB]">
          {article.featured_image && <img src={article.featured_image} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" />}
        </div>
        <div className="p-5">
          <div className="flex justify-between items-center mb-3">
            {cat && <span className="font-mono-label text-[#4A7A3D]">{cat.name}</span>}
            <span className="font-mono-label text-[#4B4B48]">{langLabel}</span>
          </div>
          <h3 className="font-display text-2xl leading-[1.08] text-[#1A1A18] group-hover:text-[#4A7A3D]">{article.title}</h3>
          {article.subtitle && <p className="mt-3 text-sm text-[#4B4B48] line-clamp-2">{article.subtitle}</p>}
          <div className="mt-4 font-mono-label text-[#4B4B48]">{article.reading_time || 4} min</div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/article/${article.slug}`} className="group block card-shift border border-[#E0D8CE]" data-testid={`article-card-${article.slug}`}>
      <div className="aspect-[4/3] overflow-hidden bg-[#F5EFEB]">
        {article.featured_image && <img src={article.featured_image} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" />}
      </div>
      <div className="p-5">
        <div className="flex justify-between items-center mb-3">
          {cat && <span className="font-mono-label text-[#4A7A3D]">{cat.name}</span>}
          <span className="font-mono-label text-[#4B4B48]">{langLabel}</span>
        </div>
        <h3 className="font-display text-2xl leading-[1.08] text-[#1A1A18] group-hover:text-[#4A7A3D]">{article.title}</h3>
        {article.subtitle && <p className="mt-3 text-sm text-[#4B4B48] line-clamp-2">{article.subtitle}</p>}
        <div className="mt-4 font-mono-label text-[#4B4B48]">{article.reading_time || 4} min</div>
      </div>
    </Link>
  );
}
